import React from 'react'
import { Link } from '@inertiajs/react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import AdrPickupExplainer from '@/components/help/widgets/AdrPickupExplainer'
import RateTimelineWidget from '@/components/help/widgets/RateTimelineWidget'
import type { SharedProps } from '@/types/shared'

export default function RateCalendar({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Rate Calendar"
      subtitle="Your daily command center for monitoring rates, occupancy, and demand."
    >
      <GuideSection title="Overview">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The calendar is where you monitor everything. It is the first place to check after changing
          any pricing rule, and the best place to spot patterns, anomalies, or opportunities. Two tabs
          give you different lenses on the same data:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Prices</strong> — shows the final computed rate for
            each room type on each date. This is the rate that gets pushed to your PMS.
          </li>
          <li>
            <strong className="text-foreground">Occupancy</strong> — shows booking levels, demand
            tiers, and pickup momentum. Use this to understand why rates are moving the way they are.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Three view modes let you see the data differently:{' '}
          <strong className="text-foreground">Calendar</strong> (monthly grid),{' '}
          <strong className="text-foreground">Grid</strong> (room types × dates table), and{' '}
          <strong className="text-foreground">Timeline</strong> (horizontal swim lanes). We recommend
          starting with the Calendar view for a quick overview, then switching to Grid when you need to
          compare room types side by side.
        </p>
      </GuideSection>

      <GuideSection title="Reading the Prices View">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each cell shows the <strong className="text-foreground">final computed rate</strong> for that
          room type on that date. The rate displayed is exactly what gets pushed to your PMS — no
          rounding or formatting happens in the UI.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Color coding gives you an instant visual sense of how rates compare to the base rate:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-emerald-600">Green text</strong> — the final rate is above the
            base rate. Seasonal, demand, or event adjustments are pushing the price up.
          </li>
          <li>
            <strong className="text-rose-600">Rose text</strong> — the final rate is below the base
            rate. Negative modifiers or low demand are pulling the price down.
          </li>
          <li>
            <strong className="text-amber-600">Amber indicator</strong> — a manual override is active
            for this date. The displayed rate is the override value, not the pipeline-computed value.
          </li>
          <li>
            <strong className="text-slate-500">Gray background with strikethrough</strong> — stop sell
            is active. The room type cannot be booked on this date regardless of the rate.
          </li>
          <li>
            <strong className="text-blue-600">Blue border</strong> — closed to arrival (CTA) is
            active. Guests cannot check in on this date, but existing bookings spanning it are
            unaffected.
          </li>
        </ul>

        <GuideWidget
          title="Rate Timeline"
          footnote="Shows how a single room's rate evolves across multiple computation cycles."
        >
          <RateTimelineWidget />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="Reading the Occupancy View">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The Occupancy tab shows how booked your property is, date by date. Each cell is{' '}
          <strong className="text-foreground">color-coded by demand tier</strong>:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-emerald-600">Emerald (80%+ occupancy)</strong> — high demand.
            Rates should be near or at ceiling. If they are not, check whether your demand settings
            and guardrails are configured.
          </li>
          <li>
            <strong className="text-amber-600">Amber (60–79% occupancy)</strong> — moderate demand.
            The sweet spot where dynamic adjustments do most of their work.
          </li>
          <li>
            <strong className="text-slate-500">Slate (below 60% occupancy)</strong> — low demand.
            Rates may be near or at floor, or demand discounts may be pulling prices down to
            stimulate bookings.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each cell also shows the <strong className="text-foreground">occupancy percentage</strong>{' '}
          and <strong className="text-foreground">rooms fraction</strong> (e.g., "67% · 4/6"). Pickup
          indicators show booking momentum — a green arrow means bookings are accelerating compared to
          the previous period.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          For a combined inventory, a room sold on two listings (the lead listing&apos;s room and its Same
          room) is one row, counted across both, and its day details split the night&apos;s rooms sold by
          listing. The figure for the whole building, in each day&apos;s cell and on the hover card, reads{' '}
          <strong className="text-foreground">Building</strong> instead of Hotel, and ADR reads{' '}
          <strong className="text-foreground">Building ADR</strong>. See{' '}
          <Link href="/guides/occupancy-metrics#building-figures" className="text-primary hover:underline">
            Building figures
          </Link>
          .
        </p>

        <GuideWidget
          title="ADR & Pickup Explainer"
          footnote="Watch how ADR and pickup change as bookings arrive over a 30-day window."
        >
          <AdrPickupExplainer />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="Hover Card">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Hovering over any cell shows a detailed popup without leaving the calendar. The hover card
          contains:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Date and lead time</strong> — the stay date and how
            many nights away it is from today.
          </li>
          <li>
            <strong className="text-foreground">Demand label</strong> — the current demand tier (high,
            moderate, low) for that date.
          </li>
          <li>
            <strong className="text-foreground">Rooms booked/available</strong> — the occupancy
            snapshot and percentage.
          </li>
          <li>
            <strong className="text-foreground">ADR</strong> — the average daily rate for that date,
            current year and last year for comparison.
          </li>
          <li>
            <strong className="text-foreground">Pickup (last 7 days)</strong> — how many more (or
            fewer) rooms have been booked for this date over the last 7 days, shown in rooms. It can
            be negative when bookings cancel.
          </li>
          <li>
            <strong className="text-foreground">Pipeline breakdown</strong> — base rate → each
            adjustment stage → final rate. Every stage that modified the rate is shown with its
            contribution.
          </li>
          <li>
            <strong className="text-foreground">PMS sync status</strong> — whether the displayed rate
            has been successfully pushed to your PMS.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The hover card is the fastest way to answer "why is this rate what it is?" without leaving
          the calendar view.
        </p>
      </GuideSection>

      <GuideSection title="Day Detail Modal">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Click any date to open the <strong className="text-foreground">full day detail view</strong>.
          This gives you everything the hover card shows plus more room to explore.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          At the top, summary cards show:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Occupancy</strong> — percentage and room count
            (booked/available).
          </li>
          <li>
            <strong className="text-foreground">ADR</strong> — current average daily rate and
            comparison with the same date last year.
          </li>
          <li>
            <strong className="text-foreground">Pickup</strong> — 7-day, 30-day, and 60-day booking
            trends for this date.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Below, every room type is listed with its computed rate, PMS sync status, and any active
          restrictions. Expand any room type to see the{' '}
          <strong className="text-foreground">full pipeline breakdown</strong> with running totals at
          each stage. This is the most detailed view available and is ideal for investigating why a
          rate landed where it did.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The <strong className="text-foreground">Edit Prices</strong> tab lets you set manual
          overrides directly from the day detail view, without navigating away from the calendar.
          Every room reads <strong className="text-foreground">Now</strong> (what it sells at today),{' '}
          <strong className="text-foreground">Engine</strong> (what the engine would charge with no
          override), a <strong className="text-foreground">%</strong> box and an{' '}
          <strong className="text-foreground">After</strong> box. A percentage is applied to the engine
          figure, a typed price is fixed, and the line under the boxes always spells out the move:{' '}
          <em>£78 → £68 · +5% of engine £65</em>. Linked rooms show the same move with their own offset.
          A room that already has an override wears its rule on a chip — <em>Engine +20%</em> or{' '}
          <em>Fixed £90</em> — and the bin icon in its After box removes that override, taking the room
          back to the engine price; the preview shows exactly where it and its linked rooms will land.
          After you save, the form stays open until the new prices have landed.
        </p>
      </GuideSection>

      {/* COMBINED INVENTORY: the building's rooms, and where each listing's price shows (owner ruling
          2026-10-26). Static copy, the guides are property-agnostic. */}
      <GuideSection title="If Your Building Uses Combined Inventory">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The calendar shows the <strong className="text-foreground">building&apos;s rooms</strong>: the lead
          listing&apos;s rooms and the Added rooms of the following listings, once each. A following listing&apos;s
          Prices calendar opens the lead listing&apos;s. When two rooms share a name, each shows its listing in
          brackets. Occupancy, pace and the month strip describe the whole building.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          A <strong className="text-foreground">Same room</strong> is the same rooms as a room at the lead
          listing, so it is not a row of its own. Hover that room to see how the other listings sell it that
          night, for example <em>Also sold on Wharf House Wing at £138 (+15%)</em>: the price that listing
          shows and its offset. Open the day details for every listing&apos;s price that night in its{' '}
          <strong className="text-foreground">Listings</strong> section, with the night&apos;s rooms sold by
          listing. Offsets are set in the room mapping on the Combined inventory page.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          A Same room takes no manual price of its own. Set the manual price on its room at the lead listing: the
          review before you save lists each Same room at its own price, offset included.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Check the calendar after changing any pricing rule. Rates recompute within minutes, and
            the calendar is the fastest way to verify that your change had the intended effect.
          </GuideTip>
          <GuideTip type="do">
            Use the Occupancy tab to spot low-demand dates, then check if your demand settings are
            responding appropriately. If occupancy is low but rates have not dropped, your demand
            thresholds may need adjusting.
          </GuideTip>
          <GuideTip type="dont">
            Do not ignore the PMS sync status indicators. A yellow dot means rates have not been
            pushed to your PMS yet — guests are still seeing the old price until the push completes.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'how-it-works', title: 'How It Works' },
          { slug: 'overrides', title: 'Overrides' },
          { slug: 'occupancy-metrics', title: 'Occupancy Metrics' },
        ]}
      />
    </GuideLayout>
  )
}
